import Navbar from "@/components/NavBar";
import Footer from "@/components/Footer";
import React, { useEffect, useState, useMemo } from "react";
import { usePathname } from "next/navigation";
import Cookies from "js-cookie";
import B2BNavbar from "@/components/B2BNavBar";

interface MainLayoutProps {
    children: React.ReactNode;
}

interface UserData {
    tipoUtenteListino?: string;
}

export default function MainLayout({ children }: MainLayoutProps) {
    const pathname = usePathname();
    const [isB2B, setIsB2B] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    // Memoized calculations for better performance
    const showEllipse = useMemo(() =>
            pathname?.includes("rent-to-buy") || pathname?.includes("concessionari"),
        [pathname]
    );

    const isHomePage = useMemo(
        () =>
            pathname === "/" ||
            pathname === "/home" ||
            pathname === "/auth/accedi" ||
            pathname === "/auth/registrati",
        [pathname]
    );

    const mainClasses = useMemo(() => {
        const baseClasses = "min-h-screen";
        const marginTop = isHomePage ? "" : "mt-[100px]";
        return `${baseClasses} ${marginTop}`.trim();
    }, [isHomePage]);

    useEffect(() => {
        const checkUserType = () => {
            try {
                const cookieData = Cookies.get("userData");

                if (!cookieData) {
                    setIsB2B(false);
                    return;
                }

                const userData: UserData = JSON.parse(cookieData);
                setIsB2B(userData?.tipoUtenteListino === "COM");
            } catch (error) {
                console.warn("Errore nel parsing dei cookie utente:", error);
                setIsB2B(false);
            } finally {
                setIsLoading(false);
            }
        };

        checkUserType();
    }, []);

    // Loading state per evitare flash del contenuto
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className={showEllipse ? "relative w-full overflow-hidden" : ""}>
            {/* Ellipse Background */}
            {showEllipse && (
                <>
                    {/* Base background */}
                    <div
                        className="absolute top-0 left-0 w-full h-[100px] z-[-2]"
                        style={{ background: "#2d3cc1" }}
                        aria-hidden="true"
                    />
                    {/* Gradient ellipse */}
                    <div
                        className="w-[110%] h-[500px] max-sm:w-[160%] max-sm:h-[350px] absolute top-0 z-[-1] left-1/2 -translate-x-1/2"
                        style={{
                            background:
                                "radial-gradient(50% 50% at 50% 50%, #5868FF 0%, #2D3CC1 100%)",
                            borderBottomLeftRadius: "50%",
                            borderBottomRightRadius: "50%",
                        }}
                        aria-hidden="true"
                    />
                </>
            )}

            {/* Navigation */}
            <header>
                {isB2B ? (
                    <B2BNavbar />
                ) : (
                    <Navbar showEllipse={showEllipse} />
                )}
            </header>

            {/* Main Content */}
            <main className={mainClasses}>
                {children}
            </main>

            {/* Footer - solo per utenti non B2B */}
            {!isB2B && (
                <footer>
                    <Footer />
                </footer>
            )}
        </div>
    );
}
